import { useEffect, useState } from "react";
import { X, Loader2, ExternalLink, Globe } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export function ResearchModal({ open, onClose, title, query }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null); setError(null); setLoading(true);
    api.post("/ai/web-research/ask", { query })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : "Live web research is unavailable right now."))
      .finally(() => setLoading(false));
  }, [open, query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 pt-[8vh] backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-fade-up"
        onClick={(e) => e.stopPropagation()} data-testid="research-modal">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1a5fd0]"><Globe size={16} className="text-white" /></div>
          <div className="min-w-0 flex-1">
            <div className="truncate font-head text-sm font-bold text-slate-900">{title}</div>
            <div className="font-mono text-[10px] text-slate-400">Live web research · Perplexity Sonar · cited sources</div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="research-close-btn"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center gap-2 py-10 text-sm text-slate-400" data-testid="research-loading">
              <Loader2 size={16} className="animate-spin text-[#1a5fd0]" /> Researching current sources…
            </div>
          )}
          {error && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800" data-testid="research-error">{error}</div>}
          {data && (
            <div>
              <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-800" data-testid="research-answer">{data.answer}</div>
              <div className="mt-2 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-slate-400">{data.provider} · {data.model}</div>
              {data.citations?.length > 0 && (
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Sources</div>
                  {data.citations.slice(0, 8).map((url, ci) => (
                    <a key={`${url}-${ci}`} href={url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 truncate text-[11px] text-[#1a5fd0] hover:underline">
                      <ExternalLink size={10} className="shrink-0" /> <span className="truncate">{url}</span>
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-3 text-[10px] text-slate-400">General market research, not an offer or a guarantee of approval.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
